import { useState } from 'react'
import { postDBLogin } from '../api/auth'
import { toast } from 'sonner'
import type { DBLoginRequest } from '../types/auth'

export type LoginErrorType = 'invalid_credentials' | 'access_denied' | 'sso_required' | 'network' | null

interface UseDbLoginProps {
  adminLogin?: boolean
  redirectUri?: string
}

export const errorMessages: Record<NonNullable<LoginErrorType>, { title: string; desc: string }> = {
  invalid_credentials: { title: 'Invalid credentials', desc: 'Please check your email and password and try again.' },
  access_denied: { title: 'Access denied', desc: 'An administrator account is required to access this portal.' },
  sso_required: { title: 'SSO Required', desc: 'This account uses single sign-on. Please use your identity provider.' },
  network: { title: 'Network error', desc: 'Unable to connect to the authentication service. Please try again.' },
}

export function useDbLogin({ adminLogin = false, redirectUri = '/' }: UseDbLoginProps) {
  const [submitting, setSubmitting] = useState(false)
  const [errorType, setErrorType] = useState<LoginErrorType>(null)

  const login = async (credentials: Omit<DBLoginRequest, 'admin_login'>) => {
    setErrorType(null)
    setSubmitting(true)

    try {
      await postDBLogin({ ...credentials, admin_login: adminLogin })
      
      toast.success('Successfully authenticated', {
        description: 'Redirecting you to your dashboard...'
      })
      
      // Short delay so the user can see the success toast before the full page reload
      setTimeout(() => {
        window.location.replace(redirectUri)
      }, 500)
      
      return true
    } catch (err: unknown) {
      setSubmitting(false)
      let currentError: LoginErrorType = 'network'
      
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosErr = err as { response?: { status?: number; data?: { detail?: string } } }
        const status = axiosErr.response?.status
        const detail = axiosErr.response?.data?.detail ?? ''

        if (status === 401) {
          currentError = 'invalid_credentials'
        } else if (status === 403 && detail.toLowerCase().includes('single sign-on')) {
          currentError = 'sso_required'
        } else if (status === 403) {
          currentError = 'access_denied'
        }
      }
      
      setErrorType(currentError)
      toast.error(errorMessages[currentError].title, {
        description: errorMessages[currentError].desc
      })
      
      return false
    }
  }

  return {
    login,
    submitting,
    errorType,
    setErrorType
  }
}
